import type { Role, TeamMember } from "@webyonetim/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { PageHeader } from "../../components/shell/AppShell";
import { Button, SelectField } from "../../components/ui/Controls";
import { ErrorState, Skeleton, StatusBadge } from "../../components/ui/Feedback";
import { ResponsiveData, type DataColumn } from "../../components/ui/ResponsiveData";
import { useAuth } from "../../lib/auth";
import { formatDateTime } from "../../lib/format";
import { teamApi } from "../../lib/api";
import { InviteMemberDialog, RemoveMemberFlow, teamRoleDefinitions } from "./MemberDialogs";

export function TeamPage() {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const membersQuery = useQuery({
    queryKey: ["team-members"],
    queryFn: teamApi.members,
    retry: false,
  });
  const invitationsQuery = useQuery({
    queryKey: ["team-invitations"],
    queryFn: teamApi.invitations,
    retry: false,
  });
  const [inviteOpen, setInviteOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<TeamMember>();
  const canManage = auth.identity?.permissions.includes("team:manage") === true;
  const ownerCount =
    membersQuery.data?.items.filter((member) => member.membership.role === "owner").length ?? 0;
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["team-members"] });
    void queryClient.invalidateQueries({ queryKey: ["team-invitations"] });
  };
  const updateRole = async (member: TeamMember, role: Role) => {
    await teamApi.updateRole(member.membership.id, role, member.membership.revision);
    refresh();
  };
  const columns: readonly DataColumn<TeamMember>[] = [
    {
      header: "Üye",
      key: "member",
      render: (member) => (
        <>
          <strong>{member.user.displayName}</strong>
          <br />
          <span>{member.user.email}</span>
        </>
      ),
    },
    {
      header: "Rol",
      key: "role",
      render: (member) => {
        const lastOwner = member.membership.role === "owner" && ownerCount <= 1;
        return canManage ? (
          <div>
            <SelectField
              aria-label={`${member.user.email} rolü`}
              disabled={lastOwner}
              label="Rol"
              onChange={(event) => {
                void updateRole(member, event.currentTarget.value as Role);
              }}
              value={member.membership.role}
            >
              {teamRoleDefinitions.map((role) => (
                <option key={role.code} value={role.code}>
                  {role.label}
                </option>
              ))}
            </SelectField>
            {lastOwner ? (
              <p className="field__help">
                Son sahip kaldırılamaz veya daha düşük role geçirilemez.
              </p>
            ) : null}
          </div>
        ) : (
          teamRoleDefinitions.find((role) => role.code === member.membership.role)?.label
        );
      },
    },
    {
      header: "Durum",
      key: "status",
      render: (member) => (
        <StatusBadge
          label={member.membership.status === "active" ? "Etkin" : "Askıya alındı"}
          status={member.membership.status === "active" ? "active" : "warning"}
        />
      ),
    },
    {
      header: "Son etkinlik",
      key: "active",
      render: (member) => (
        <time dateTime={member.user.updatedAt}>{formatDateTime(member.user.updatedAt)}</time>
      ),
    },
    {
      header: "İşlemler",
      key: "actions",
      render: (member) =>
        canManage && !(member.membership.role === "owner" && ownerCount <= 1) ? (
          <Button
            onClick={() => {
              setRemoveTarget(member);
            }}
            variant="destructive"
          >
            Ekipten çıkar
          </Button>
        ) : (
          <span className="muted">İşlem yok</span>
        ),
    },
  ];
  return (
    <div className="page">
      <PageHeader
        action={
          canManage ? (
            <Button
              onClick={() => {
                setInviteOpen(true);
              }}
            >
              Ekip üyesi davet et
            </Button>
          ) : undefined
        }
        description="Kuruluma erişen kullanıcıları ve sabit rollerini yönetin."
        title="Ekip"
      />
      {membersQuery.isLoading || invitationsQuery.isLoading ? <Skeleton /> : null}
      {membersQuery.isError || invitationsQuery.isError ? <ErrorState onRetry={refresh} /> : null}
      {membersQuery.data === undefined ? null : (
        <section className="card">
          <ResponsiveData
            caption="Ekip üyeleri"
            columns={columns}
            getKey={(member) => member.membership.id}
            items={membersQuery.data.items}
            mobileHeading={(member) => member.user.email}
          />
        </section>
      )}
      {invitationsQuery.data !== undefined && invitationsQuery.data.items.length > 0 ? (
        <section className="card stack">
          <h2>Bekleyen davetler</h2>
          <ul>
            {invitationsQuery.data.items.map((invite) => (
              <li key={invite.id}>
                {invite.email} ·{" "}
                {teamRoleDefinitions.find((role) => role.code === invite.role)?.label} ·{" "}
                <StatusBadge label="Bekliyor" status="queued" />
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      <InviteMemberDialog onCreated={refresh} onOpenChange={setInviteOpen} open={inviteOpen} />
      {removeTarget === undefined ? null : (
        <RemoveMemberFlow
          email={removeTarget.user.email}
          membershipId={removeTarget.membership.id}
          onClosed={() => {
            setRemoveTarget(undefined);
          }}
          onRemoved={refresh}
          open
          requireSecondFactor={auth.identity?.user.totpEnabled === true}
        />
      )}
    </div>
  );
}
